import ModalPage from '@/components/ui/Modal';
import useSizeController from '@/hooks/useSizeController';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch, SetStateAction } from 'react';

interface TravelersData {
    adults: number;
    children: number[];
    infants: number[];
}


interface FlightTravelersModalProps {
    showModal: boolean;
    onClose: () => void
    setTravelersData: Dispatch<SetStateAction<TravelersData>>;
    travelersData: TravelersData;
}

const FlightTravelersModal = ({
    showModal,
    setTravelersData,
    travelersData,
    onClose
}: FlightTravelersModalProps) => {

    const { isSmall } = useSizeController()

    return isSmall ?
        <ModalPage isFull identifier='Travelers' onClose={onClose} show={showModal} >
            <div>
                <div className='grid pb-4 items-center grid-cols-[24px,_1fr] gap-2'>
                    <button onClick={onClose} className='text-gray-500 size-6'>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <span>Passengers &  Cabin Class</span>

                </div>
                <Body setTravelersData={setTravelersData} travelersData={travelersData} />
            </div>
        </ModalPage>
        : (
            <div
                className={`${showModal
                    ? "z-20 opacity-100 md:translate-y-full"
                    : " z-0 !h-0 overflow-y-auto opacity-0 md:translate-y-[96%]"
                    } z-10 flex h-fit w-full min-w-fit max-h-[50vh]
              flex-col overflow-y-auto rounded-xl duration-300 md:absolute
              md:bottom-0 md:w-80 md:border md:bg-white md:p-3 md:shadow md:transition-all`}
            >

                <Body setTravelersData={setTravelersData} travelersData={travelersData} />
            </div>
        );
};

export default FlightTravelersModal;

function Body({
    setTravelersData,
    travelersData,
}: {
    setTravelersData: Dispatch<SetStateAction<TravelersData>>;
    travelersData: TravelersData;
}) {

    const handleReduceOneAdult = () => {
        setTravelersData((prevData) => {

            if (prevData.adults > 1) {
                return {
                    ...prevData,
                    adults: prevData.adults - 1,
                };
            }
            return prevData; // Cannot reduce below 1 adult
        });
    };

    // Handler to add one adult
    const handleAddOneAdult = () => {
        setTravelersData((prevData) => {
            // Limit the number of adults (e.g., to 10)
            if (prevData.adults >= 10) {
                return prevData; // Cannot add more than 10 adults
            }
            return {
                ...prevData,
                adults: prevData.adults + 1,
            };
        });
    };

    // Handler to reduce the number of children
    const handleReduceChildren = () => {
        setTravelersData((prevData) => {
            const updatedChildren = [...prevData.children]; // Create a copy
            if (updatedChildren.length > 0) {
                updatedChildren.pop(); // Remove the last child's age
            }
            return {
                ...prevData,
                children: updatedChildren,
            };
        });
    };

    // Handler to add a child
    const handleAddChildren = () => {
        setTravelersData((prevData) => {
            // Limit the number of children (e.g., to 10)
            if (prevData.children.length >= 10) {
                return prevData; // Cannot add more than 10 children
            }
            // Add a default age (e.g., 2) for the new child
            const updatedChildren = [...prevData.children, 2];
            return {
                ...prevData,
                children: updatedChildren,
            };
        });
    };

    // Handler to change a specific child's age
    const handleChangeChildAge = (
        childIndex: number,
        newAge: number,
    ) => {
        setTravelersData((prevData) => {
            const updatedChildren = [...prevData.children]; // Create a copy
            updatedChildren[childIndex] = newAge; // Update the age at the specific index
            return {
                ...prevData,
                children: updatedChildren,
            };
        });
    };

    // Handler to reduce the number of infants
    const handleReduceInfants = () => {
        setTravelersData((prevData) => {
            const updatedInfants = [...prevData.infants]; // Create a copy
            if (updatedInfants.length > 0) {
                updatedInfants.pop(); // Remove the last infant's age
            }
            return {
                ...prevData,
                infants: updatedInfants,
            };
        });
    };

    // Handler to add an infant
    const handleAddInfant = () => {
        setTravelersData((prevData) => {
            // Limit the number of infants (e.g., to 10)
            if (prevData.infants.length >= 10) {
                return prevData; // Cannot add more than 10 infants
            }
            // Add a default age (e.g., 0) for the new infant
            const updatedInfants = [...prevData.infants, 0];
            return {
                ...prevData,
                infants: updatedInfants,
            };
        });
    };

    // Handler to change a specific infant's age
    const handleChangeInfantAge = (
        infantIndex: number,
        newAge: number,
    ) => {
        setTravelersData((prevData) => {
            const updatedInfants = [...prevData.infants]; // Create a copy
            updatedInfants[infantIndex] = newAge; // Update the age at the specific index
            return {
                ...prevData,
                infants: updatedInfants,
            };
        });
    };

    return <div>
        {/* Adults Counter */}
        <div className="border-b border-gray-200 pb-4 mb-4">
            <div className="mt-2 flex items-center justify-between">
                <div className="font-normal">
                    <p className="text-sm font-semibold text-gray-800">Adults</p>
                    <p className="text-xs text-gray-500 font-normal">12 and older</p>
                </div>
                <div className="flex max-w-[115px] grow items-center justify-between border text-gray-600
                                         border-gray-100 rounded-2xl p-2">
                    <button
                        onClick={handleReduceOneAdult}
                        type="button"
                        className={`${travelersData.adults <= 1 ? 'bg-gray-200' : 'bg-secondary-500'} aspect-square rounded-xl w-8 text-lg border text-white px-2`}
                        disabled={travelersData.adults <= 1} // Disable button if only 1 adult
                    >
                        -
                    </button>
                    <span className="">{travelersData.adults}</span>
                    <button
                        onClick={handleAddOneAdult}
                        type="button"
                        className="aspect-square rounded-xl w-8 text-lg border bg-secondary-500 text-white px-2"
                        disabled={travelersData.adults >= 10} // Disable button if max adults reached
                    >
                        +
                    </button>
                </div>
            </div>
        </div>

        {/* Children Counter */}
        <div className=" border-gray-200 pb-4 ">
            <div className="mt-2 flex items-center justify-between">
                <div className="font-normal">
                    <p className="text-sm font-semibold text-gray-800">Children</p>
                    <p className="text-xs text-slate-500">Ages 2-12</p>
                </div>
                <div className="flex max-w-[115px] grow items-center justify-between text-gray-600 border border-gray-100 rounded-2xl p-2">
                    <button
                        type="button"
                        onClick={handleReduceChildren}
                        className={`${travelersData.children.length === 0 ? 'bg-gray-200' : 'bg-secondary-500'} aspect-square rounded-xl w-8 text-lg border text-white px-2`}
                        disabled={travelersData.children.length === 0} // Disable button if no children
                    >
                        -
                    </button>
                    <span className="">{travelersData.children.length}</span>
                    <button
                        type="button"
                        onClick={handleAddChildren}
                        className="aspect-square rounded-xl w-8 text-lg border bg-secondary-500 text-white px-2"
                        disabled={travelersData.children.length >= 10} // Disable button if max children reached
                    >
                        +
                    </button>
                </div>
            </div>
            {/* Children Age Selectors */}
            {travelersData.children.length > 0 && (
                <div className="mt-3 grid md:grid-cols-2 text-gray-600 gap-3 pt-3">
                    {travelersData.children.map((childAge, childIndex) => (
                        <div className='grid items-center gap-y-2 grid-cols-[1fr,_auto] md:grid-cols-1 ' key={childIndex}>
                            <h4 className="mb-1 text-xs text-gray-500">
                                Child {childIndex + 1} age
                            </h4>
                            <select
                                onChange={(e) =>
                                    handleChangeChildAge(
                                        childIndex,
                                        Number(e.target.value),
                                    )
                                }
                                className="w-full rounded-md border bg-slate-100 p-1"
                                value={childAge}
                            >
                                {/* Options for child ages 2 through 12 */}
                                {Array.from({ length: 11 }, (_, i) => i + 2).map(age => (
                                    <option key={age} value={age}>{age}</option>
                                ))}
                            </select>
                        </div>
                    ))}
                </div>
            )}
        </div>

        {/* Infants Counter */}
        <div className="pb-4  last:mb-0 border-t"> {/* Removed border-b from last item */}
            <div className="mt-2 flex items-center justify-between">
                <div className="font-normal">
                    <p className="text-sm font-semibold text-gray-800">Infants</p>
                    <p className="text-xs text-slate-500">Ages 0-2 </p>
                </div>
                <div className="flex max-w-[115px] grow items-center justify-between text-gray-600 border border-gray-100 rounded-2xl p-2">
                    <button
                        type="button"
                        onClick={handleReduceInfants}
                        className={`${travelersData.infants.length === 0 ? 'bg-gray-200' : 'bg-secondary-500'} aspect-square rounded-xl w-8 text-lg border text-white px-2`}
                        disabled={travelersData.infants.length === 0}
                    >
                        -
                    </button>
                    <span className="">{travelersData.infants.length}</span>
                    <button
                        type="button"
                        onClick={handleAddInfant}
                        className="aspect-square rounded-xl w-8 text-lg border bg-secondary-500 text-white px-2"
                        disabled={travelersData.infants.length >= 10}
                    >
                        +
                    </button>
                </div>
            </div>
            {/* Infants Age Selectors */}
            {travelersData.infants.length > 0 && (
                <div className="mt-3 grid md:grid-cols-2 text-gray-600 gap-3 pt-3">
                    {travelersData.infants.map((infantAge, infantIndex) => (
                        <div className='grid items-center gap-y-2 grid-cols-[1fr,_auto] md:grid-cols-1 ' key={infantIndex}>
                            <h4 className="mb-1 text-xs text-gray-500">
                                Infant {infantIndex + 1} age
                            </h4>
                            <select
                                onChange={(e) =>
                                    handleChangeInfantAge(
                                        infantIndex,
                                        Number(e.target.value),
                                    )
                                }
                                className="w-full rounded-md border bg-slate-100 p-1"
                                value={infantAge}
                            >
                                {/* Options for infant ages 0 through 2 */}
                                {Array.from({ length: 3 }, (_, i) => i).map(age => (
                                    <option key={age} value={age}>{age}</option>
                                ))}
                            </select>
                        </div>
                    ))}
                </div>
            )}
        </div>
    </div>
}
